import {useEffect, useMemo, useRef, useState} from "react";
import {useUrlBackground} from "hooks/useBackgroundNumbers";
import {useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";

import {useEdits} from "contexts/edits";
import {useImages, BG_TRANSFORM_FIELDS} from "contexts/images";
import {useSetting} from "contexts/setting";
import {isEmptyObj} from "libs/utils";
import {applySceneBgSource} from "libs/sceneBgSource";
import {track} from "libs/analytics";
import {API} from "api";

export function useScene() {
  const [isSceneLoaded, setIsSceneLoaded] = useState(false);
  const {sceneId} = useParams();
  // Latest-ref pattern: read the URL's background (hooks/useBackgroundNumbers)
  // at fire-time, but don't make the effect re-fire when it changes.
  // BgSelector.onBgButtonClick writes it on every bg click; if `urlBg` were a
  // dep, every click would re-dispatch loadEditsScene with the original
  // scene data and clobber the user's unsaved text edits via
  // editsReducer's LOAD_SCENE case (edits.js:71-78). Same hazard for
  // checkIfAnimated re-runs on customBg scenes.
  const urlBackground = useUrlBackground().background;
  const urlBgRef = useRef(urlBackground);
  urlBgRef.current = urlBackground;

  const {loadScene: loadEditsScene} = useEdits();
  const {loadScene: loadImagesScene} = useImages();
  const {loadScene: loadSettingScene} = useSetting();

  // In render mode, the server injects scene data into window.__SCENE_DATA__
  // before SPA navigation (Phase A optimization). This eliminates the
  // browser→backend API round-trip (~200-500ms).
  const injectedData = typeof window !== "undefined" ? window.__SCENE_DATA__ : undefined;

  const {
    isLoading,
    isPending,
    isSuccess,
    isFetching,
    isError,
    error,
    status,
    fetchStatus,
    refetch,
    data: fetchedData,
  } = useQuery({
    queryKey: ["fetch-scene", sceneId],
    queryFn: async () => {
      const scene = await API.fetchScene(sceneId);
      if (!scene) {
        const notFoundError = new Error(`Scene not found: ${sceneId}`);
        notFoundError.status = 404;
        throw notFoundError;
      }
      return scene;
    },
    enabled: Boolean(sceneId) && !injectedData,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // A scene saved under an admin's reordered gallery pins the IMAGE in
  // `bgSource`; `bgOption` alone would select whatever now stands at that
  // position for this reader. Applied once, here, so every consumer of `data`
  // — the three context loaders, the render path, the callers below — sees the
  // same background. A scene without the field passes through untouched.
  const rawData = injectedData || fetchedData;
  const data = useMemo(() => applySceneBgSource(rawData), [rawData]);

  useEffect(() => {
    if (sceneId && (injectedData || (isSuccess && !isFetching)) && data) {
      loadSettingScene(sceneId, data);
      loadEditsScene(data);

      // URL `?background=N` overrides the saved bgOption. The scene's saved
      // bg-transforms are bound to its saved bg — they don't apply to a
      // different one. Drop them from the payload so BgSelector's
      // URL-driven `transformBg(N)` (which writes to the same fields) can't
      // be clobbered by a late-returning scene/load/v2. Without this, the
      // dual writers race and the saved transforms occasionally win,
      // showing a mispositioned bg until the user clicks (PR #94 / commit
      // a825ab0 fixed this for synchronous mounts but not for the
      // network-late case — surfaced as a ~2% post-deploy VR flake on
      // scene-bg-override.spec.cjs).
      const urlBg = urlBgRef.current;
      const sceneBg = String(data.bgOption ?? "1");
      const imagesData =
        urlBg && urlBg !== sceneBg
          ? Object.fromEntries(
              Object.entries(data).filter(([k]) => !BG_TRANSFORM_FIELDS.includes(k))
            )
          : data;
      loadImagesScene(imagesData);

      // contentType (like product/channel) must come from the scene's saved
      // data, not auto-enrichment. The setAnalyticsContext useEffect in
      // SettingContextProvider runs after the next render — at this point
      // the singleton still holds the previous selection, so reusing it
      // would tag the event with stale contentType when loading a scene
      // saved for a different type.
      track("neurobaker.scene.loaded", {
        sceneId,
        product: data.product?.value,
        channel: data.channel?.value,
        contentType: data.type?.value,
      });
      setIsSceneLoaded(true);
    }
  }, [
    sceneId,
    injectedData,
    isSuccess,
    isFetching,
    data,
    loadSettingScene,
    loadEditsScene,
    loadImagesScene,
  ]);

  const isLoaded = useMemo(() => {
    return (
      sceneId && (injectedData || (isSuccess && !isFetching)) && !isEmptyObj(data) && isSceneLoaded
    );
  }, [sceneId, injectedData, isSuccess, isFetching, isSceneLoaded, data]);

  return {
    isLoaded,
    isError,
    error,
    status,
    fetchStatus,
    isFetching,
    isLoading,
    isPending,
    isSuccess,
    refetch,
    data,
  };
}
